import { camera_cloud_auth } from 'src/stores/features/cameras/cloud-auth';
import { getGStorageSignedUrl } from './recordings';
import { get } from 'svelte/store';

/** Buffer time before expiration to trigger refresh (5 minutes in ms) */
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

/** In-flight refresh promise to prevent concurrent refresh calls */
let refreshPromise: Promise<string | null> | null = null;

/**
 * Check if the current signed URL is expired or about to expire
 */
function isExpiredOrExpiring(cameraCloudAuthState): boolean {
	// console.log('[gcs-auth] isExpiredOrExpiring', cameraCloudAuthState);
	if (!cameraCloudAuthState.token || !cameraCloudAuthState.expiresAt) {
		return true;
	}

	const expiresAt = new Date(cameraCloudAuthState.expiresAt).getTime();
	const now = Date.now();

	// Consider expired if within buffer time of expiration
	return now >= expiresAt - EXPIRY_BUFFER_MS;
}

/**
 * Refresh the signed URL from the API
 */
async function refreshSignedUrl(): Promise<string | null> {
	try {
		const response = await getGStorageSignedUrl();
		// console.log('[gcs-auth] API response:', JSON.stringify(response, null, 2));

		camera_cloud_auth.setFromResponse(response);
		// console.log('[gcs-auth] Stored token:', response.token);
		return response.token;
	} catch (error) {
		console.error('Failed to refresh camera cloud signed URL', error);
		camera_cloud_auth.clear();
		return null;
	}
}

/**
 * Get a valid signed URL for camera cloud storage access
 * Automatically refreshes if expired or about to expire
 *
 * @returns The signed URL or null if unable to obtain one
 */
export async function getCameraCloudSignedUrl(): Promise<string | null> {
	const currentState = get(camera_cloud_auth);

	// If not expired, return current token
	if (!isExpiredOrExpiring(currentState)) {
		// console.log('[gcs-auth] Using cached token');
		return currentState.token;
	}

	console.log('[gcs-auth] SignedUrl expired or missing, refreshing...');

	// If already refreshing, wait for that promise
	if (refreshPromise) {
		// console.log('[gcs-auth] Waiting for existing refresh promise');
		return refreshPromise;
	}

	// Start refresh and cache the promise to prevent concurrent calls
	// console.log('[gcs-auth] Starting new refresh');
	refreshPromise = refreshSignedUrl().finally(() => {
		refreshPromise = null;
	});

	return refreshPromise;
}

/**
 * Force refresh the signed URL (useful after login or when URL becomes invalid)
 *
 * @returns The new signed URL or null if unable to obtain one
 */
export async function forceRefreshCameraCloudSignedUrl(): Promise<string | null> {
	// If already refreshing, wait for that promise
	if (refreshPromise) {
		return refreshPromise;
	}

	refreshPromise = refreshSignedUrl().finally(() => {
		refreshPromise = null;
	});

	return refreshPromise;
}

/**
 * Check if we have a valid (non-expired) signed URL
 */
export function hasCameraCloudAuth(): boolean {
	return !isExpiredOrExpiring(get(camera_cloud_auth));
}
